"use client";
import { useState, useEffect, useRef } from 'react';
import { Bars3Icon, XMarkIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { Link, useLocation } from 'react-router-dom';

const primaryNav = [
  { name: 'Home',            href: '/' },
  { name: 'About',           href: '/about-us' },
  { name: 'Features',        href: '/features' },
  { name: 'Case Precedents', href: '/case-precedents' },
  { name: 'Get Involved',    href: '/get-involved' },
  { name: "Report Incident",   href: '/report-incident' },
];

const moreNav = [
  { name: 'FAQs',    href: '/faqs' },
  { name: 'Contact', href: '/contact-us' },
];

const allNav = [...primaryNav, ...moreNav];

const featuredNav = { name: 'Live Intelligence', href: '/dashboard' };

export default function PageHeader() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [moreOpen, setMoreOpen]             = useState(false);
  const [scrolled, setScrolled]             = useState(false);
  const { pathname } = useLocation();
  const moreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 48);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) setMoreOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <>
      <header
        style={{ zIndex: 9999, fontFamily: 'var(--font-body)' }}
        className={`sticky inset-x-0 top-0 transition-all duration-500 ${
          scrolled
            ? 'bg-white/95 glass shadow-sm border-b border-green-100'
            : 'bg-white border-b border-green-50'
        }`}
      >
        <nav className="flex items-center justify-between px-4 lg:px-10 h-16" aria-label="Global">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 shrink-0">
            <img className="h-10 w-auto" src="/ICON2.png" alt="WITS" />
            <div className="hidden md:block leading-tight">
              <p
                className="text-xs font-bold tracking-widest text-gray-900 uppercase"
                style={{ fontFamily: 'var(--font-display)' }}
              >
                Wildlife Information
              </p>
              <p
                className="text-xs font-bold tracking-widest text-green-700 uppercase"
                style={{ fontFamily: 'var(--font-display)' }}
              >
                &amp; Tracking System
              </p>
            </div>
            <p
              className="md:hidden text-sm font-bold tracking-widest text-gray-900 uppercase"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              WITS
            </p>
          </Link>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-5">
            {primaryNav.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`nav-link text-xs font-medium whitespace-nowrap pb-0.5 transition-colors duration-200 ${
                  pathname === item.href
                    ? 'text-green-700 active'
                    : 'text-gray-600 hover:text-green-700'
                }`}
              >
                {item.name}
              </Link>
            ))}

            {/* More dropdown */}
            <div className="relative" ref={moreRef}>
              <button
                onClick={() => setMoreOpen((v) => !v)}
                className="flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-green-700 transition-colors duration-200 whitespace-nowrap"
              >
                More <ChevronDownIcon className={`w-3 h-3 transition-transform duration-200 ${moreOpen ? 'rotate-180' : ''}`} />
              </button>
              {moreOpen && (
                <div className="absolute top-full left-0 mt-2 w-36 rounded-xl border border-gray-100 bg-white shadow-lg shadow-gray-200/60 py-1 z-50">
                  {moreNav.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setMoreOpen(false)}
                      className={`block px-4 py-2.5 text-xs font-medium transition-colors ${
                        pathname === item.href ? 'text-green-700 bg-green-50' : 'text-gray-600 hover:text-green-700 hover:bg-gray-50'
                      }`}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Desktop CTAs */}
          <div className="hidden lg:flex items-center gap-3 shrink-0">
            <Link
              to={featuredNav.href}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full border text-xs font-medium transition-all duration-300 whitespace-nowrap ${
                pathname === featuredNav.href
                  ? 'bg-green-700 border-green-700 text-white'
                  : 'border-green-600 text-green-700 hover:bg-green-700 hover:text-white hover:border-green-700'
              }`}
            >
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500" />
              </span>
              {featuredNav.name}
            </Link>
            <a
              href="https://lapa.africa/donate"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold hover:bg-amber-100 transition-all duration-300 whitespace-nowrap"
            >
              <svg className="w-3 h-3 fill-amber-500 shrink-0" viewBox="0 0 24 24"><path d="M12 21.593c-.525-.400-6.84-5.054-8.717-7.486C1.26 11.834 1.003 9.726 1.588 8.017c.585-1.703 2.04-2.963 3.757-3.164 1.718-.2 3.388.688 4.655 2.172 1.267-1.484 2.937-2.372 4.655-2.172 1.717.201 3.172 1.461 3.757 3.164.585 1.709.328 3.817-1.695 6.09C16.84 16.539 12.525 21.193 12 21.593z"/></svg>
              Donate
            </a>
            <Link
              to="/accounts/login"
              className="px-4 py-2 rounded-full bg-[#052e16] text-white text-xs font-medium hover:bg-green-800 transition-all duration-300 hover:shadow-lg hover:shadow-green-900/20 whitespace-nowrap"
            >
              Sign In →
            </Link>
          </div>

          {/* Mobile menu toggle */}
          <button
            type="button"
            className="lg:hidden p-2 rounded-md text-gray-700 hover:text-green-700"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Open menu"
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
        </nav>
      </header>

      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-[99999]">
          <button
            type="button"
            aria-label="Close menu"
            className="absolute inset-0 w-full h-full bg-black/50 backdrop-blur-sm cursor-default"
            onClick={() => setMobileMenuOpen(false)}
            onKeyDown={(e) => e.key === 'Escape' && setMobileMenuOpen(false)}
          />
          <div
            className="absolute inset-y-0 right-0 w-80 flex flex-col px-6 py-6"
            style={{ background: '#052e16' }}
          >
            {/* Drawer header */}
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <img className="h-9 w-auto" src="/ICON2.png" alt="WITS" />
                <span
                  className="text-xs font-bold text-white/80 tracking-widest uppercase"
                  style={{ fontFamily: 'var(--font-display)' }}
                >
                  WITS
                </span>
              </div>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="text-white/60 hover:text-white p-1 transition-colors"
                aria-label="Close menu"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Nav links */}
            <nav className="flex flex-col gap-1 flex-1">
              {allNav.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                    pathname === item.href
                      ? 'bg-green-600/20 text-green-400'
                      : 'text-white/70 hover:text-white hover:bg-white/8'
                  }`}
                  style={{ fontFamily: 'var(--font-body)' }}
                >
                  {item.name}
                </Link>
              ))}
            </nav>

            {/* CTAs */}
            <div className="pt-6 border-t border-white/10 space-y-3">
              <Link
                to={featuredNav.href}
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl border border-green-400/50 text-green-400 text-sm font-medium hover:bg-green-400/10 transition-all"
                style={{ fontFamily: 'var(--font-body)' }}
              >
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500" />
                </span>
                {featuredNav.name}
              </Link>
              <Link
                to="/report-incident"
                onClick={() => setMobileMenuOpen(false)}
                className="block text-center py-2.5 px-4 rounded-xl border border-white/20 text-white/70 text-sm font-medium hover:bg-white/8 transition-all"
                style={{ fontFamily: 'var(--font-body)' }}
              >
                Report Incident
              </Link>
              <a
                href="https://lapa.africa/donate"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-amber-500/15 border border-amber-400/30 text-amber-300 text-sm font-semibold hover:bg-amber-500/25 transition-all"
                style={{ fontFamily: 'var(--font-body)' }}
              >
                <svg className="w-3.5 h-3.5 fill-amber-400" viewBox="0 0 24 24"><path d="M12 21.593c-.525-.400-6.84-5.054-8.717-7.486C1.26 11.834 1.003 9.726 1.588 8.017c.585-1.703 2.04-2.963 3.757-3.164 1.718-.2 3.388.688 4.655 2.172 1.267-1.484 2.937-2.372 4.655-2.172 1.717.201 3.172 1.461 3.757 3.164.585 1.709.328 3.817-1.695 6.09C16.84 16.539 12.525 21.193 12 21.593z"/></svg>
                Donate to Our Cause
              </a>
              <Link
                to="/accounts/login"
                onClick={() => setMobileMenuOpen(false)}
                className="block text-center py-2.5 px-4 rounded-full bg-green-600 text-white text-sm font-semibold hover:bg-green-500 transition-all"
                style={{ fontFamily: 'var(--font-body)' }}
              >
                Sign In →
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
